import { fitBounds } from 'google-map-react';
import { maxBy, minBy, uniqWith } from 'lodash/fp';

import { DEFAULT_CENTER, DEFAULT_ZOOM } from 'dashboard/components/Map';
import { MIN_ZOOM } from 'dashboard/components/Map/Map';
import { Place, SerializedPlace } from 'lib/interfaces/place';
import Size from 'lib/interfaces/size';

/**
 * Height is hardcoded; use approximate width for default mapWidth -- doesn't have to be super precise
 * We are not able to dynamically calculate the dimensions until after the component
 * is already rendered so we have to hardcode and estimate
 */
const APPROXIMATE_MAP_WIDTH = 750;

export function serializePlace({ lat, lng }: Place): SerializedPlace {
  return `${lat} ${lng}` as SerializedPlace;
}

export function deserializePlace(place: SerializedPlace): Place {
  const [lat, lng] = place.split(' ').map(Number);
  return {
    lat: lat,
    lng: lng,
  };
}

/**
 * Extract the highest and lowest lat/lng and generate NW and SE corners.
 * These are used as the defaults for map center and zoom to ensure as many
 * markers as possible are displayed by default.
 */
export function computeInitialMapCenterAndZoom(
  mapHeight: number,
  locations: Place[],
  mapWidth: number = APPROXIMATE_MAP_WIDTH,
) {
  const dedupedLocations = uniqWith((a, b) => a.lat === b.lat && a.lng === b.lng, locations);

  if (dedupedLocations.length === 0) {
    return {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
    };
  } else if (dedupedLocations.length === 1) {
    if (dedupedLocations[0].radiusMeters) {
      return computeCenterAndZoomForPlace(
        { height: mapHeight, width: mapWidth },
        dedupedLocations[0],
      );
    }
  } else {
    const { center, zoom } = fitBounds(
      {
        ne: {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          lat: maxBy('lat', dedupedLocations).lat,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          lng: maxBy('lng', dedupedLocations).lng,
        },
        sw: {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          lat: minBy('lat', dedupedLocations).lat,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          lng: minBy('lng', dedupedLocations).lng,
        },
      },
      { height: mapHeight, width: mapWidth },
    );

    // If the calculated bounding box requires an impossible zoom level, fall
    // back to the "one location" strategy. This happens if locations are too
    // far apart, e.g. 1 in NA and 1 in Asia.
    if (zoom > MIN_ZOOM) {
      return { center, zoom };
    }
  }

  return {
    center: { lat: dedupedLocations[0].lat, lng: dedupedLocations[0].lng },
    zoom: DEFAULT_ZOOM,
  };
}

export function computeCenterAndZoomForPlace(mapSize: Size, location: Place) {
  const center = { lat: location.lat, lng: location.lng };
  const { zoom } = fitBounds(
    {
      ne: {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        lat: location.lat + latitudeFromMeters(location.radiusMeters),
        lng:
          location.lng +
          longitudeFromMetersAndLatitude(
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            location.radiusMeters,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            latitudeFromMeters(location.radiusMeters),
          ),
      },
      sw: {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        lat: location.lat - latitudeFromMeters(location.radiusMeters),
        lng:
          location.lng -
          longitudeFromMetersAndLatitude(
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            location.radiusMeters,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            latitudeFromMeters(location.radiusMeters),
          ),
      },
    },
    { width: mapSize.width, height: mapSize.height },
  );

  return {
    center,
    zoom: Math.max(zoom, MIN_ZOOM),
  };
}

// 1 degree latitude is always 111.32km
const LATITUDE_TO_METER_CONVERSION = 111320;

function latitudeFromMeters(meters: number) {
  return meters / LATITUDE_TO_METER_CONVERSION;
}

// Multiplication factor to convert longitude to distance
// Formula is 1 degree longitude = 40075 km * (cos(latitude) / 360)
const LONGITUDE_TO_METER_MULTIPLIER = 40075000;

function longitudeFromMetersAndLatitude(meters: number, lat: number) {
  return meters / ((LONGITUDE_TO_METER_MULTIPLIER * Math.cos(lat)) / 360);
}
